import { GatsbyImage, IGatsbyImageData, getImage } from 'gatsby-plugin-image';
import { Link } from 'gatsby';
import DOMPurify from 'isomorphic-dompurify';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

import '../../styles/generic.css';
import '../../styles/bordered-grid-list.css';

import { _ } from '../../utils/l10n';
import ArrowFwdPrimary from '../../assets/icons/arrow-fwd-primary.svg';

export type TwoColBorderedGridItem = {
  /**
   * Grid item title
   *
   * @todo make optional
   */
  title: string;
  /**
   * Grid item text
   */
  text: string;
  /**
   * Grid item image
   */
  image: { publicURL: string } | IGatsbyImageData;
  /**
   * Grid item image alt text
   */
  image_alt?: string;
  /**
   * Grid item link url
   */
  link_url?: string;
  /**
   * Grid item link label
   */
  link_label?: string;
  /**
   * Label for the optional tag displayed next to the item title
   */
  tag_label?: string;
};

interface TwoColBorderedGridListProps {
  /**
   * Items list
   */
  items: TwoColBorderedGridItem[];
}

const isGatsbyImage = (imageToCheck: TwoColBorderedGridItem['image']): imageToCheck is IGatsbyImageData => (
  !!(imageToCheck as IGatsbyImageData)?.images
)

export const TwoColBorderedGridList = ({ items }: TwoColBorderedGridListProps) => (
  <ul className='bordered-grid-list'>
    {items.map(({ title, text, image, image_alt, link_url, link_label, tag_label }) => {
      let img;
      if (image) {
        if (isGatsbyImage(image)) {
          const imageData = getImage(image);
          img = imageData && (
            <GatsbyImage
              image={imageData}
              alt={image_alt ?? ''}
              loading='lazy'
              className='bordered-grid-list-img'
            />
          );
        } else {
          img = <img src={image.publicURL} alt={image_alt} className='bordered-grid-list-img' />;
        }
      }
      return (
        <li key={title} className='bordered-grid-list-item'>
          {img}
          <div className='bordered-grid-list-title-container'>
            <h4 className='bordered-grid-list-title'>{title}</h4>
            {!!tag_label && <span className="filled-tag">{tag_label}</span>}
          </div>
          <div className='bordered-grid-list-desc'>
            <Markdown rehypePlugins={[rehypeRaw]}>{text}</Markdown>
          </div>
          {link_url && link_label && (
            <div className='bordered-grid-list-link link-container'>
              <Link to={link_url} className='primary-text link'>
                <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(link_label) }} />
                <img src={ArrowFwdPrimary} alt='Arrow forward' className='link-icon' />
              </Link>
            </div>
          )}
        </li>
      );
    })}
  </ul>
);
